// src/components/CaseStudies.jsx
import { useTranslation } from "react-i18next";
import LocalizedLink from "./LocalizedLink";
import { CASES } from "../data/content";

export default function CaseStudies() {
  const { t } = useTranslation();
  const featured = CASES.slice(0, 2); // homepage: only first two

  const TT = (key, fallback) => {
    if (!key) return fallback;
    const v = t(key);
    return v === key ? fallback : v;
  };

  return (
    <section id="projects" className="py-16 bg-slate-50">
      <div className="mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100">
              {TT("projects.band", "Case studies")}
              <span className="inline-block size-1 rounded-full bg-indigo-300" />
              {TT("projects.tagline", "Real apps, real results")}
            </span>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight">
              {t("projects.heading")}
            </h2>
            <p className="mt-2 text-slate-600">{t("projects.subheading_home")}</p>
          </div>

          <LocalizedLink
            to="/projects"
            className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-white/70"
          >
            {t("projects.see_all")}
          </LocalizedLink>
        </div>

        {/* Grid */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {featured.map((c) => {
            const title = TT(c.titleKey, "");
            const tag = TT(c.tagKey, "");
            const summary = TT(c.summaryKey, "");

            return (
              <article
                key={c.slug}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                {/* Make entire card clickable */}
                <LocalizedLink
                  to={`/projects/${c.slug}`}
                  aria-label={title}
                  className="absolute inset-0 z-10"
                />

                {/* Visual area */}
                <div className="relative h-40 bg-gradient-to-br from-slate-50 to-indigo-50">
                  <div className="absolute inset-0 grid place-items-center p-6">
                    {c.logo ? (
                      <img
                        src={c.logo}
                        alt={title}
                        loading="lazy"
                        className="max-h-24 max-w-[72%] object-contain transition-transform duration-300 group-hover:scale-[1.02]"
                      />
                    ) : (
                      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
                        <span className="text-lg font-bold text-slate-700">
                          {initials(title)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-indigo-400 via-fuchsia-400 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </div>

                {/* Body */}
                <div className="relative p-6">
                  {!!tag && (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
                      {tag}
                    </span>
                  )}
                  <h3 className="mt-3 text-xl font-semibold text-slate-900">
                    {title}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-slate-600">{summary}</p>

                  {c.result && (
                    <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
                      <span className="inline-block size-1.5 rounded-full bg-emerald-400" />
                      {c.result}
                    </p>
                  )}

                  <div className="mt-4 flex items-center gap-2 text-indigo-600">
                    <span className="font-medium">{t("projects.read_more")}</span>
                    <svg
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12.293 4.293a1 1 0 011.414 0L18 8.586a1 1 0 010 1.414l-4.293 4.293a1 1 0 01-1.414-1.414L14.586 10H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" />
                    </svg>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* Helpers */
function initials(str = "") {
  try {
    return str
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");
  } catch {
    return "—";
  }
}
